<script lang="ts">
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore.js";
	import type { ContactFormInput } from "../../../../core/schema/ContactForm.js";
	import sendContactForm from "../../api/mutations/sendContactForm.graphql";
	import ContactFields from "../Form/ContactFields.svelte";
	import FormFooter from "../Form/FormFooter.svelte";
	import Button from "../Button.svelte";
	import Modal from "../Modal.svelte";
	import Form from "../Form/Form.svelte";
	import { mutation } from "../../api/graphql.js";
	import translations from "./CareerForm.translations.json";
	import FileUploader from "../Form/FileUploader.svelte";
	import type { UploadedFile } from "../../../../core/schema/UploadedFile.js";
	import type { Attachment } from "../../../../core/schema/Attachment.js";
	import type { Position } from "../../../../core/schema/Position.js";
	import { ContactFormType } from "../../../../core/schema/ContactFormType.js";
	import { createEventDispatcher } from "svelte";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { getTenant } from "../../getTenant.js";
	import { getTranslate } from "../../utils/getTranslate.js";
	import { readContactFormData } from "../Form/readContactFormData.js";

	export let position: Position | undefined = undefined;

	const translate = getTranslate(translations);
	const dispatch = createEventDispatcher<{
		// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
		close: void;
	}>();

	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();

	const tenant = getTenant();

	const send = createPromiseStore(async (input: ContactFormInput) => {
		await mutation<boolean>(PUBLIC_GRAPHQL_ENDPOINT, sendContactForm, { input });
	});

	let uploadedFile: UploadedFile | undefined;
	let showSuccessModal = false;
	let finished = false;
	async function submit(event: CustomEvent<SubmitEvent>): Promise<void> {
		const form = event.detail.target as HTMLFormElement;
		const attachment: Attachment | undefined = uploadedFile && {
			...uploadedFile,
			title: uploadedFile.title ?? "CV",
		};

		const input: ContactFormInput = {
			...readContactFormData(new FormData(form), tenant.locale),
			file: attachment,
			position: position?.id,
			type: ContactFormType.Career,
		};
		await send.call(input);
		showSuccessModal = true;
		finished = true;
	}

	let form: HTMLFormElement | null = null;
	$: {
		if (finished && !showSuccessModal) {
			form?.reset();
			uploadedFile = undefined;
			dispatch("close");
		}
	}
</script>

<Modal bind:show={showSuccessModal}>
	<p class="h2" slot="title">{translate("thanks")}</p>
	<div class="flex flex-col items-center gap-2">
		<p>{translate("willBeInTouch")}</p>

		<Button
			on:click={() => {
				showSuccessModal = false;
			}}
		>
			{translate("understand")}
		</Button>
	</div>
</Modal>

<div class="flex flex-col">
	<div class="border-lttertiary border-b px-8 pb-8">
		<p class="font-lexend !mb-0 max-w-lg !text-base font-normal leading-[1.6] tracking-normal text-black">
			{translate("desc")}
		</p>
	</div>

	<Form bind:form id="career-form" on:submit={submit}>
		<ContactFields idPrefix="career-" textareaClass="h-40" />

		<div class="border-lttertiary border-b p-8">
			<FileUploader
				destinationFolder="cv"
				currentFiles={uploadedFile ? [uploadedFile] : []}
				on:change={({ detail: { files } }) => {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					uploadedFile = files;
				}}
				required
				white
				border={false}
			>
				<span
					class="font-inter mx-auto block max-w-96 text-center text-xs/[1.4] font-normal italic tracking-normal text-[#333333]"
				>
					{translate("uploadHint")}
				</span>
			</FileUploader>
		</div>

		<div class="flex flex-col gap-6 p-8">
			<FormFooter
				loading={$send.status === "loading"}
				error={$send.status === "error"}
				rowFrom={null}
				class="!gap-6"
				checkboxClass="!mt-0 justify-start"
			/>
		</div>
	</Form>
</div>
